import { FileMeta } from '../lib/api';
import { ManifestDoc, ManifestAction, ManifestFile } from '../lib/manifestModel';
import { isAllowedDest } from '../lib/whitelist';

type Props = {
  doc: ManifestDoc;
  setDoc: (d: ManifestDoc) => void;
  files: FileMeta[];
};

const ACTION_WHEN: ManifestAction['when'][] = ['once', 'always', 'on-change'];

export function ManifestForms({ doc, setDoc, files }: Props) {
  function updateFile(i: number, patch: Partial<ManifestFile>) {
    const next = doc.files.slice();
    next[i] = { ...next[i], ...patch };
    setDoc({ ...doc, files: next });
  }
  function addFile() {
    setDoc({
      ...doc,
      files: [
        ...doc.files,
        { name: 'new-file', src: files[0]?.path || '', dest: '', mode: '0644', owner: 'root:root' },
      ],
    });
  }
  function removeFile(i: number) {
    setDoc({ ...doc, files: doc.files.filter((_, idx) => idx !== i) });
  }
  function updateAction(i: number, patch: Partial<ManifestAction>) {
    const next = doc.actions.slice();
    next[i] = { ...next[i], ...patch };
    setDoc({ ...doc, actions: next });
  }
  function addAction() {
    setDoc({
      ...doc,
      actions: [
        ...doc.actions,
        { name: 'new-action', when: 'always', command: 'true' },
      ],
    });
  }
  function removeAction(i: number) {
    setDoc({ ...doc, actions: doc.actions.filter((_, idx) => idx !== i) });
  }

  const fileNames = doc.files.map((f) => f.name);

  return (
    <div className="space-y-6">
      <section className="card">
        <div className="flex justify-between items-center mb-3">
          <h2 className="font-semibold">Files</h2>
          <button className="btn" onClick={addFile}>+ Add file</button>
        </div>
        <table className="table">
          <thead>
            <tr>
              <th>Name</th><th>Source file</th><th>Destination</th>
              <th>Mode</th><th>Owner</th><th></th>
            </tr>
          </thead>
          <tbody>
            {doc.files.length === 0 && (
              <tr><td colSpan={6} className="text-muted">No files. Click "Add file".</td></tr>
            )}
            {doc.files.map((f, i) => {
              const destOk = typeof f.dest === 'string' && f.dest.length > 0 && isAllowedDest(f.dest);
              return (
                <tr key={i}>
                  <td>
                    <input className="input w-32" value={f.name}
                      onChange={(e) => updateFile(i, { name: e.target.value })} />
                  </td>
                  <td>
                    <select className="select w-56" value={f.src}
                      onChange={(e) => updateFile(i, { src: e.target.value })}>
                      <option value="">— select uploaded file —</option>
                      {files.map((u) => <option key={u.id} value={u.path}>{u.path}</option>)}
                    </select>
                  </td>
                  <td>
                    <input
                      className={`input w-64 ${!destOk ? 'border-danger' : ''}`}
                      value={f.dest ?? ''}
                      onChange={(e) => updateFile(i, { dest: e.target.value })}
                      placeholder="/etc/ssl/…"
                    />
                    {!destOk && <div className="text-xs text-danger">Must start with /etc/ssl/, /etc/ssl/priv/, /etc/ssl/keys/, or /usr/local/bin/</div>}
                  </td>
                  <td>
                    <input className="input w-20" value={f.mode || ''} placeholder="0644"
                      onChange={(e) => updateFile(i, { mode: e.target.value })} />
                  </td>
                  <td>
                    <input className="input w-28" value={f.owner || ''} placeholder="root:root"
                      onChange={(e) => updateFile(i, { owner: e.target.value })} />
                  </td>
                  <td>
                    <button className="btn btn-danger" onClick={() => removeFile(i)}>Remove</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      <section className="card">
        <div className="flex justify-between items-center mb-3">
          <h2 className="font-semibold">Actions</h2>
          <button className="btn" onClick={addAction}>+ Add action</button>
        </div>
        <p className="text-xs text-muted mb-2">
          ⚠ Commands run as <code>root</code> on every matching host. Review carefully.
        </p>
        <table className="table">
          <thead>
            <tr>
              <th>Name</th><th>When</th><th>Trigger (on-change only)</th>
              <th>Command</th><th></th>
            </tr>
          </thead>
          <tbody>
            {doc.actions.length === 0 && (
              <tr><td colSpan={5} className="text-muted">No actions.</td></tr>
            )}
            {doc.actions.map((a, i) => (
              <tr key={i}>
                <td>
                  <input className="input w-32" value={a.name}
                    onChange={(e) => updateAction(i, { name: e.target.value })} />
                </td>
                <td>
                  <select className="select w-28" value={a.when}
                    onChange={(e) => updateAction(i, { when: e.target.value as ManifestAction['when'] })}>
                    {ACTION_WHEN.map((w) => <option key={w} value={w}>{w}</option>)}
                  </select>
                </td>
                <td>
                  <select
                    multiple
                    className="select w-48 h-20"
                    disabled={a.when !== 'on-change'}
                    value={a.trigger || []}
                    onChange={(e) => {
                      const sel = Array.from(e.target.selectedOptions).map((o) => o.value);
                      updateAction(i, { trigger: sel });
                    }}
                  >
                    {fileNames.map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </td>
                <td>
                  <textarea
                    className="textarea w-72"
                    rows={2}
                    value={a.command ?? ''}
                    onChange={(e) => updateAction(i, { command: e.target.value })}
                  />
                </td>
                <td>
                  <button className="btn btn-danger" onClick={() => removeAction(i)}>Remove</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
